import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Activity, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError(t("password_min")); return; }
    if (password !== confirm) { setError(t("passwords_mismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/auth"), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-base text-white flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <Activity className="w-6 h-6 text-signal" strokeWidth={2.5} />
          <span className="font-heading font-black text-xl tracking-tight">SNETCH</span>
        </Link>

        {done ? (
          <div data-testid="reset-done" className="text-center">
            <CheckCircle2 className="w-14 h-14 text-profit mx-auto mb-6" />
            <h1 className="font-heading font-black text-2xl mb-2">{t("reset_done")}</h1>
            <p className="font-mono text-sm text-white/50 mb-8">{t("reset_done_desc")}</p>
            <Link to="/auth">
              <Button data-testid="reset-login-btn" className="bg-signal text-black hover:bg-white rounded-sm font-semibold">
                {t("back_to_login")}
              </Button>
            </Link>
          </div>
        ) : !token ? (
          <div className="text-center">
            <h1 className="font-heading font-black text-2xl mb-2">{t("reset_title")}</h1>
            <p data-testid="reset-invalid" className="font-mono text-sm text-loss mb-8">{t("reset_invalid")}</p>
            <Link to="/forgot-password">
              <Button className="bg-signal text-black hover:bg-white rounded-sm font-semibold">{t("forgot_btn")}</Button>
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-heading font-black text-3xl tracking-tight mb-2">{t("reset_title")}</h1>
            <div className="w-12 h-1 bg-signal mb-6" />
            <p className="font-mono text-sm text-white/50 mb-6">{t("reset_desc")}</p>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-wider">{t("new_password")}</Label>
                <Input
                  data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="mt-1.5 bg-panel border-white/10 text-white focus-visible:ring-signal rounded-sm"
                />
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-wider">{t("confirm_password")}</Label>
                <Input
                  data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
                  className="mt-1.5 bg-panel border-white/10 text-white focus-visible:ring-signal rounded-sm"
                />
              </div>
              {error && <p data-testid="reset-error" className="text-loss text-sm font-mono">{error}</p>}
              <Button
                data-testid="reset-submit" type="submit" disabled={loading}
                className="w-full bg-signal text-black hover:bg-white rounded-sm font-semibold h-11"
              >
                {loading ? "…" : t("reset_btn")}
              </Button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
